import { useState, useRef, useEffect } from 'react';
import api from '../../api';

// Чи схожий ввід на телефон (а не на ім'я): перший непробільний символ — цифра або '+'.
function looksLikePhone(val) {
  const t = String(val || '').trim();
  return t.length > 0 && (/[0-9]/.test(t[0]) || t[0] === '+');
}

// Нормалізація українського номера до єдиного формату +380XXXXXXXXX.
// Зводимо ввід до абонентської частини (9 цифр) і завжди додаємо префікс +380:
//  • 380XXXXXXXXX / 38… / часткове «3» → відкидаємо код країни
//  • 0XXXXXXXXX (нац. формат)         → відкидаємо ведучий 0
//  • одразу абонентська частина (50…, 67…) → лишаємо як є
// Завдяки цьому «3» дає «+380», а не «+3803».
function normalizePhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (!d) return '+380';
  let sub;
  if (d.startsWith('380')) sub = d.slice(3);
  else if (d.startsWith('38')) sub = d.slice(2);
  else if (d.startsWith('3')) sub = d.slice(1);   // частковий ввід коду країни «3…»
  else if (d.startsWith('0')) sub = d.slice(1);   // нац. формат 0XX…
  else sub = d;                                    // вже абонентська частина
  return '+380' + sub.slice(0, 9);                 // абонентська частина = 9 цифр
}

// Повний валідний номер під Україну: +380 і ще 9 цифр.
function isCompletePhone(normalized) {
  return /^\+380\d{9}$/.test(normalized);
}

export default function CustomerSearch({ customer, onSelect, onClear, onBonusChange, subtotal, maxSpendFrac = 0.3 }) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [bonusToUse, setBonusToUse] = useState('');
  const [adding, setAdding] = useState(false);     // показ форми «додати клієнта»
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Скидаємо бонуси коли міняється клієнт або сума
  useEffect(() => {
    setBonusToUse('');
    onBonusChange('0');
  }, [customer?.id]);

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleInput(val) {
    // Якщо ввід схожий на телефон — приводимо до єдиного формату +380XXXXXXXXX.
    // Ім'я (починається з літери) лишаємо як є — пошук працює і за іменем.
    const value = looksLikePhone(val) ? normalizePhone(val) : val;
    setInput(value);
    setAdding(false);
    setAddError('');
    clearTimeout(debounceRef.current);
    const clean = value.replace(/[\s\-\(\)\+]/g, '');
    if (clean.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/pos/customer/search?q=${encodeURIComponent(value)}`);
        setSuggestions(res.data);
        setShowSuggestions(res.data.length > 0);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
  }

  // Додати нового клієнта в базу (номер уже нормалізований у полі вводу).
  async function handleAddCustomer() {
    const phone = normalizePhone(input);
    if (!isCompletePhone(phone)) { setAddError('Невірний номер'); return; }
    setAddLoading(true); setAddError('');
    try {
      const res = await api.post('/customers/', { phone, full_name: newName.trim() || null });
      onSelect(res.data);
      setInput(res.data.phone);
      setAdding(false);
      setNewName('');
      setShowSuggestions(false);
      setSuggestions([]);
    } catch (err) {
      setAddError(err.response?.data?.detail || 'Не вдалося додати клієнта');
    } finally { setAddLoading(false); }
  }

  function selectCustomer(c) {
    onSelect(c);
    setInput(c.phone);
    setShowSuggestions(false);
    setSuggestions([]);
    setBonusToUse('');
    setAdding(false);
    onBonusChange('0');
  }

  function handleClear() {
    onClear();
    setInput('');
    setSuggestions([]);
    setBonusToUse('');
    setAdding(false);
    setNewName('');
    setAddError('');
    onBonusChange('0');
  }

  function handleBonusChange(val) {
    const num = parseFloat(val) || 0;
    const max = Math.min(
      parseFloat(customer?.bonus_balance || 0),
      subtotal * maxSpendFrac
    );
    const clamped = Math.min(num, max);
    // Якщо касир ввів більше дозволеного — візуально виправляємо поле на максимум.
    setBonusToUse(num > max ? String(Math.round(max * 100) / 100) : val);
    onBonusChange(String(clamped));
  }

  function applyMaxBonus() {
    const max = Math.min(
      parseFloat(customer?.bonus_balance || 0),
      subtotal * maxSpendFrac
    ).toFixed(2);
    setBonusToUse(max);
    onBonusChange(max);
  }

  const maxBonus = customer
    ? Math.min(parseFloat(customer.bonus_balance), subtotal * maxSpendFrac).toFixed(2)
    : '0';

  return (
    <div style={styles.wrap} ref={wrapRef}>
      {!customer ? (
        <div>
          <div style={styles.inputWrap}>
            <input
              style={styles.input}
              placeholder="📞 Телефон або ім'я клієнта..."
              value={input}
              onChange={e => handleInput(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            />
            {loading && <span style={styles.spinner}>⟳</span>}
          </div>

          {showSuggestions && (
            <div style={styles.suggestions}>
              {suggestions.map(c => (
                <div key={c.id} style={styles.suggestion} onClick={() => selectCustomer(c)}>
                  <div style={styles.suggLeft}>
                    <div style={styles.suggPhone}>
                      {c.is_blacklisted && '🚫 '}
                      {c.age_verified && '✅ '}
                      {c.phone}
                    </div>
                    <div style={styles.suggName}>{c.full_name || 'Без імені'}</div>
                  </div>
                  <div style={styles.suggBonus}>🎁 {parseFloat(c.bonus_balance).toFixed(0)}</div>
                </div>
              ))}
            </div>
          )}

          {!loading && (() => {
            const phone = normalizePhone(input);
            const complete = isCompletePhone(phone);
            const exact = suggestions.some(c => normalizePhone(c.phone) === phone);
            // Повний номер, якого немає в базі → пропонуємо додати клієнта.
            if (complete && !exact) {
              return adding ? (
                <div style={styles.addBox}>
                  <div style={styles.addTitle}>Новий клієнт: {phone}</div>
                  <input
                    style={styles.input}
                    placeholder="Ім'я (необов'язково)"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddCustomer()}
                    autoFocus
                  />
                  {addError && <div style={styles.addError}>{addError}</div>}
                  <div style={styles.addBtns}>
                    <button style={styles.addCancel} onClick={() => { setAdding(false); setNewName(''); setAddError(''); }}>Скасувати</button>
                    <button style={styles.addSave} disabled={addLoading} onClick={handleAddCustomer}>
                      {addLoading ? '...' : '✓ Додати'}
                    </button>
                  </div>
                </div>
              ) : (
                <button style={styles.addPrompt} onClick={() => setAdding(true)}>
                  ➕ Додати {phone} в базу клієнтів
                </button>
              );
            }
            // Інакше — звичайна підказка «не знайдено» (напр. пошук за іменем).
            if (input.replace(/[\s\-()+]/g, '').length >= 3 && suggestions.length === 0 && !showSuggestions) {
              return <div style={styles.notFound}>Клієнта не знайдено — продаж без бонусів</div>;
            }
            return null;
          })()}
        </div>
      ) : (
        <div style={styles.customerCard}>
          {customer.is_blacklisted && (
            <div style={styles.blacklistWarn}>🚫 Клієнт в чорному списку!</div>
          )}
          <div style={styles.customerTop}>
            <div style={{flex:1}}>
              <div style={styles.customerName}>
                {customer.age_verified && '✅ '}
                {customer.full_name || 'Без імені'}
              </div>
              <div style={styles.customerPhone}>{customer.phone}</div>
            </div>
            <div style={styles.balanceBox}>
              <div style={styles.balanceNum}>{parseFloat(customer.bonus_balance).toFixed(2)}</div>
              <div style={styles.balanceLabel}>бонусів</div>
            </div>
            <button style={styles.clearBtn} onClick={handleClear}>✕</button>
          </div>

          {parseFloat(customer.bonus_balance) > 0 && subtotal > 0 && (
            <div style={styles.bonusRow}>
              <span style={styles.bonusLabel}>Списати:</span>
              <input
                style={styles.bonusInput}
                type="number"
                step="0.01"
                min="0"
                max={maxBonus}
                placeholder="0"
                value={bonusToUse}
                onChange={e => handleBonusChange(e.target.value)}
              />
              <span style={styles.bonusCurrency}>₴</span>
              <button style={styles.maxBtn} onClick={applyMaxBonus}>
                Макс {maxBonus} ₴
              </button>
            </div>
          )}
          {parseFloat(bonusToUse) > 0 && (
            <div style={styles.bonusApplied}>
              ✓ Знижка {parseFloat(bonusToUse) > parseFloat(maxBonus) ? maxBonus : parseFloat(bonusToUse).toFixed(2)} ₴
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { position:'relative', padding:'10px 12px', borderBottom:'1px solid #38301f', flexShrink:0 },
  inputWrap: { position:'relative' },
  input: { width:'100%', padding:'9px 12px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'13px', boxSizing:'border-box' },
  spinner: { position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', color:'#9a8a6e' },
  suggestions: { position:'absolute', left:'12px', right:'12px', background:'#1c1812', border:'1px solid #f5a623', borderRadius:'8px', zIndex:100, overflow:'hidden', marginTop:'4px', boxShadow:'0 4px 20px rgba(0,0,0,0.5)' },
  suggestion: { padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #38301f', display:'flex', alignItems:'center', gap:'10px' },
  suggLeft: { flex:1 },
  suggPhone: { fontWeight:'bold', fontSize:'13px' },
  suggName: { color:'#9a8a6e', fontSize:'12px' },
  suggBonus: { color:'#f5a623', fontSize:'12px', background:'#38301f', padding:'2px 8px', borderRadius:'10px', whiteSpace:'nowrap' },
  notFound: { color:'#9a8a6e', fontSize:'12px', marginTop:'6px', textAlign:'center' },
  addPrompt: { width:'100%', marginTop:'6px', padding:'9px 12px', background:'#38301f', color:'#f5a623', border:'1px dashed #f5a623', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'bold' },
  addBox: { marginTop:'6px', background:'#1c1812', border:'1px solid #f5a623', borderRadius:'8px', padding:'10px 12px', display:'flex', flexDirection:'column', gap:'8px' },
  addTitle: { color:'#fff', fontSize:'13px', fontWeight:'bold' },
  addError: { color:'#ff6b6b', fontSize:'12px' },
  addBtns: { display:'flex', gap:'8px' },
  addCancel: { flex:1, padding:'8px', background:'#211c14', color:'#9a8a6e', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px' },
  addSave: { flex:2, padding:'8px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px', fontWeight:'bold' },
  customerCard: { background:'#1c1812', borderRadius:'8px', padding:'10px 12px' },
  blacklistWarn: { color:'#ff6b6b', fontWeight:'bold', fontSize:'12px', marginBottom:'8px', background:'#3d0000', padding:'5px 8px', borderRadius:'6px' },
  customerTop: { display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' },
  customerName: { fontWeight:'bold', fontSize:'13px', marginBottom:'2px' },
  customerPhone: { color:'#9a8a6e', fontSize:'12px' },
  balanceBox: { textAlign:'center', background:'#211c14', borderRadius:'8px', padding:'5px 10px', flexShrink:0 },
  balanceNum: { fontSize:'16px', fontWeight:'bold', color:'#f5a623' },
  balanceLabel: { fontSize:'9px', color:'#9a8a6e' },
  clearBtn: { background:'transparent', border:'none', color:'#9a8a6e', cursor:'pointer', fontSize:'16px', padding:'0 4px', flexShrink:0 },
  bonusRow: { display:'flex', alignItems:'center', gap:'6px' },
  bonusLabel: { color:'#9a8a6e', fontSize:'12px', whiteSpace:'nowrap' },
  bonusInput: { width:'65px', padding:'5px 8px', borderRadius:'6px', border:'1px solid #38301f', background:'#211c14', color:'#fff', fontSize:'13px', textAlign:'center' },
  bonusCurrency: { color:'#9a8a6e', fontSize:'12px' },
  maxBtn: { padding:'5px 8px', background:'#38301f', color:'#5fbf8f', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'11px', whiteSpace:'nowrap' },
  bonusApplied: { color:'#5fbf8f', fontSize:'11px', marginTop:'5px' },
};
